<script setup lang="ts">
/**
 * 発注ステータスバッジ
 */
import { defineProps, withDefaults, computed } from 'vue';
import { i18n } from '@/i18n';
import SvgLoader from '@/utils/SvgLoader.vue';

const props = withDefaults(
  defineProps<{
    orderStatusStr?: string;
    isShowOrderStatus?: boolean;
  }>(),
  {
    orderStatusStr: '',
    isShowOrderStatus: true,
  }
);

const emits = defineEmits<{
  (e: 'click', isShowOrderStatus: boolean): void;
}>();

const t = i18n.t;

const iconName = computed(() => {
  if (props.isShowOrderStatus) {
    switch (props.orderStatusStr) {
      case 'QuantityConfirmed':
        return 'iconEyeEnabledBlue';
      case 'DeliveryDateConfirmed':
        return 'iconEyeEnabledBlue';
      default:
        return 'iconEyeEnabled';
    }
  }
  switch (props.orderStatusStr) {
    case 'QuantityConfirmed':
      return 'iconEyeDisableBlue';
    case 'DeliveryDateConfirmed':
      return 'iconEyeDisableBlue';
    default:
      return 'iconEyeDisable';
  }
});

/**
 * クリック時の処理
 */
const onClick = () => {
  // 現在の表示状態の逆の値を親に通知する
  emits('click', !props.isShowOrderStatus);
};
</script>

<template>
  <button
    type="button"
    :class="`status-tag__container status-tag__container--${orderStatusStr}`"
    :style="{ opacity: props.isShowOrderStatus ? 1 : 0.4 }"
    @click="onClick"
  >
    <span :class="`status-tag__content status-tag__content--${orderStatusStr}`">
      {{ t(orderStatusStr) }}
    </span>
    <SvgLoader class="status-tag__icon" :name="iconName" />
  </button>
</template>

<style lang="scss" scoped>
.status-tag {
  &__container {
    display: grid;
    grid-template-columns: repeat(2, auto);
    gap: 2px;
    place-items: center;
    border-radius: 90px;
    height: 22px;
    padding-left: 14px;
    padding-right: 14px;

    &:hover {
      cursor: pointer;
      opacity: 0.8;
      transition: opacity 0.3s;
    }

    &--NoRecommendation {
      background-color: $semanticWhite; // ありえない
    }

    &--Recommended {
      background-color: $optionalOrange;
    }

    &--ApprovalRequested {
      background-color: $optionalPink;
    }
    &--ApprovedAwaitingDeliveryDate {
      background-color: $optionalViolet;
    }

    &--DeliveryDateConfirmed {
      border-color: $semanticPrimary;
      border-width: 1px;
    }

    &--QuantityConfirmed {
      border-color: $semanticPrimary;
      border-width: 1px;
    }

    &--PartialDelivery {
      background-color: $optionalCyan;
    }

    &--Confirmed {
      background-color: $semanticSuccess;
    }

    &--Delivered {
      background-color: $optionalLightBlue;
    }

    &--DeliveryDelayed {
      background-color: $semanticError;
    }

    &--DisplayEnded {
      background-color: $semanticWhite;
      border: 1px solid $borderDefault;
    }

    // Unknownの時は、アラートを表示
    &--Unknown {
      background-color: $semanticError;
    }
  }

  &__content {
    height: 12px;
    color: $semanticWhite;
    font-family: Noto Sans JP;
    font-size: 11px;
    font-weight: 700;
    line-height: 12px;
    letter-spacing: 0em;
    text-align: center;

    &--DeliveryDateConfirmed {
      color: $semanticPrimary;
    }

    &--QuantityConfirmed {
      color: $semanticPrimary;
    }

    &--DisplayEnded {
      color: $textSecondary;
    }
  }

  &__icon {
    width: 14px;
    height: 12px;
    display: grid;
    place-items: center;
  }
}
</style>
